/**
 * Deterministische reductie-transformaties voor diagram-vereenvoudiging
 * (05-backlog "Deterministische diagram-vereenvoudiging"). Pure functies:
 * gelijke input → gelijke output, geen randomness, geen I/O.
 *
 * De transforms bepalen alleen WELKE concepten verborgen worden (een set
 * concept-ids); de web-laag past dat toe op een KOPIE van het diagram (nieuw
 * diagram met `hidden` op de gekozen viewState-entries), zodat het origineel
 * intact blijft. Fase 1 dekt de drie transforms die op `hidden` leunen:
 * hide-leaves, critical-path en cluster-by-composition. Fase 2 voegt
 * layer-collapse toe (`layerCollapseMembers` + synthetische super-node in de
 * web-laag); promote-to-abstraction volgt op hetzelfde fundament.
 */

export interface SimplifyRelationRef {
  id: string;
  typeId: string;
  sourceConceptId: string;
  targetConceptId: string;
}

export interface SimplifyInput {
  /** Concept-ids die (zichtbaar) op het diagram staan. */
  conceptIds: readonly string[];
  /** Relaties op het diagram (beide eindpunten aanwezig). */
  relations: readonly SimplifyRelationRef[];
}

export interface SimplifyOptions {
  /** Verberg concepten zonder uitgaande relatie (read-only eindpunten). */
  hideLeaves?: boolean;
  /** Rol Composition/Aggregation-parts op in hun whole. */
  clusterByComposition?: boolean;
  /** Toon alleen het (kortste) pad tussen twee concepten; verberg de rest. */
  criticalPath?: { sourceId: string; targetId: string } | null;
  /**
   * Fase 2 — layer-collapse: vouw deze lagen samen tot één synthetische
   * super-node per laag. NIET verwerkt door `computeHiddenConcepts` (die
   * kent geen concept→laag-mapping); de web-laag (`createSimplifiedCopy`)
   * combineert `layerCollapseMembers` met de registry en maakt de
   * super-node-viewState-entries aan.
   */
  collapseLayers?: string[];
}

/**
 * Whole-part-relaties. Deze app slaat ze op als `source = part, target = whole`
 * (zie `archimate-emitter.ts` `SWAP_FOR_PLANTUML`). Voor cluster-by-composition
 * verbergen we dus de SOURCE-kant (de part), en houden we de whole.
 */
const WHOLE_PART_TYPES = new Set(["archimate.Composition", "archimate.Aggregation"]);

/**
 * Bereken de set concept-ids die door de gekozen transforms verborgen worden.
 * Meerdere transforms combineren via unie (elk voegt hides toe). Critical-path
 * verbergt het complement van het gevonden pad; vindt 'ie geen pad, dan doet
 * die transform niets (diagram niet leegmaken).
 */
export function computeHiddenConcepts(input: SimplifyInput, options: SimplifyOptions): Set<string> {
  const present = new Set(input.conceptIds);
  // Alleen relaties tussen twee aanwezige concepten tellen mee.
  const rels = input.relations.filter(
    (r) => present.has(r.sourceConceptId) && present.has(r.targetConceptId),
  );
  const hidden = new Set<string>();

  if (options.hideLeaves) {
    for (const id of hideLeaves(present, rels)) hidden.add(id);
  }
  if (options.clusterByComposition) {
    for (const id of clusterByComposition(rels)) hidden.add(id);
  }
  if (options.criticalPath) {
    const keep = criticalPath(
      present,
      rels,
      options.criticalPath.sourceId,
      options.criticalPath.targetId,
    );
    if (keep) {
      for (const id of present) if (!keep.has(id)) hidden.add(id);
    }
  }
  return hidden;
}

/**
 * Layer-collapse (Fase 2): bepaal per samen te vouwen laag de member-set.
 * Pure functie — de caller (web-laag) levert de concept→laag-mapping (uit de
 * registry) en maakt zelf de synthetische super-node-viewState-entries aan.
 * Lagen zonder members op het diagram leveren geen entry op. Deterministisch:
 * members behouden de volgorde van `conceptIds`.
 */
export function layerCollapseMembers(
  conceptIds: readonly string[],
  conceptLayers: Readonly<Record<string, string>>,
  layers: readonly string[],
): Map<string, string[]> {
  const wanted = new Set(layers);
  const out = new Map<string, string[]>();
  for (const id of conceptIds) {
    const layer = conceptLayers[id];
    if (!layer || !wanted.has(layer)) continue;
    const arr = out.get(layer);
    if (arr) arr.push(id);
    else out.set(layer, [id]);
  }
  return out;
}

/**
 * Promote-to-abstraction (Fase 2): detecteer realisatie-clusters. Een cluster
 * = één ApplicationService (of ander service-type) + de Components/Functions
 * die 'm realiseren. ArchiMate slaat Realization op als `source = realiserend
 * (concreet), target = gerealiseerd (abstract)`; de service is dus de TARGET.
 * Pure functie — de caller resolvet subtype→base-typeId vooraf en levert de
 * concept→typeId-map. Alleen clusters met ≥2 leden komen terug. Deterministisch:
 * services in relatie-volgorde, leden = [service, ...realizers-in-volgorde].
 */
export interface RealizationCluster {
  /** De service (abstractie-anker); wordt default-label van het blok. */
  anchorId: string;
  memberIds: string[];
}

// Beide sets noemen zowel het 3.x-type als z'n merge-doel. Zonder
// `archimate.Service` en `archimate.Function` vond de detectie na de
// ArchiMate-4-merge nul clusters: de data bevat die generieke types, de sets
// keken naar `ApplicationService` / `ApplicationFunction` die niet meer
// voorkomen. De 3.x-ids blijven staan voor teruggezette snapshots van vóór
// de flip.
//
// Bewust GEEN filter op het «Application Service»-profiel: een cluster rond
// een business- of technologiedienst is net zo goed een abstractie. De merge
// maakt de detectie dus iets ruimer, en dat is hier winst — de modelleur
// vinkt zelf aan welk voorstel 'ie overneemt.
const DEFAULT_SERVICE_TYPE_IDS = new Set(["archimate.ApplicationService", "archimate.Service"]);
const DEFAULT_REALIZER_TYPE_IDS = new Set([
  "archimate.ApplicationComponent",
  "archimate.ApplicationFunction",
  "archimate.Function",
]);

export function detectRealizationClusters(
  conceptTypeIds: Readonly<Record<string, string>>,
  relations: readonly SimplifyRelationRef[],
  serviceTypeIds: ReadonlySet<string> = DEFAULT_SERVICE_TYPE_IDS,
  realizerTypeIds: ReadonlySet<string> = DEFAULT_REALIZER_TYPE_IDS,
): RealizationCluster[] {
  const byService = new Map<string, string[]>();
  for (const r of relations) {
    if (r.typeId !== "archimate.Realization") continue;
    const service = r.targetConceptId;
    const realizer = r.sourceConceptId;
    if (!serviceTypeIds.has(conceptTypeIds[service] ?? "")) continue;
    if (!realizerTypeIds.has(conceptTypeIds[realizer] ?? "")) continue;
    const arr = byService.get(service);
    if (arr) {
      if (!arr.includes(realizer)) arr.push(realizer);
    } else {
      byService.set(service, [realizer]);
    }
  }
  const out: RealizationCluster[] = [];
  for (const [service, realizers] of byService) {
    const memberIds = [service, ...realizers];
    if (memberIds.length >= 2) out.push({ anchorId: service, memberIds });
  }
  return out;
}

/** Leaf = concept dat van geen enkele relatie de bron is (geen uitgaande relatie). */
function hideLeaves(present: Set<string>, rels: readonly SimplifyRelationRef[]): Set<string> {
  const hasOutgoing = new Set<string>();
  for (const r of rels) hasOutgoing.add(r.sourceConceptId);
  const hidden = new Set<string>();
  for (const id of present) if (!hasOutgoing.has(id)) hidden.add(id);
  return hidden;
}

/** Verberg elke concept dat als part (source) van een Composition/Aggregation optreedt. */
function clusterByComposition(rels: readonly SimplifyRelationRef[]): Set<string> {
  const hidden = new Set<string>();
  for (const r of rels) {
    if (WHOLE_PART_TYPES.has(r.typeId)) hidden.add(r.sourceConceptId);
  }
  return hidden;
}

/**
 * Kortste pad tussen twee concepten via ongerichte BFS over de relatie-graaf.
 * Returnt de set concept-ids op het pad, of `null` als er geen pad is (of een
 * eindpunt niet op 't diagram staat) — dan verbergt de caller niets.
 */
function criticalPath(
  present: Set<string>,
  rels: readonly SimplifyRelationRef[],
  sourceId: string,
  targetId: string,
): Set<string> | null {
  if (!present.has(sourceId) || !present.has(targetId)) return null;
  if (sourceId === targetId) return new Set([sourceId]);

  const adj = new Map<string, string[]>();
  const link = (a: string, b: string) => {
    const list = adj.get(a);
    if (list) list.push(b);
    else adj.set(a, [b]);
  };
  for (const r of rels) {
    link(r.sourceConceptId, r.targetConceptId);
    link(r.targetConceptId, r.sourceConceptId);
  }

  const prev = new Map<string, string>();
  const visited = new Set<string>([sourceId]);
  const queue: string[] = [sourceId];
  let head = 0;
  while (head < queue.length) {
    const cur = queue[head++];
    if (cur === undefined) break;
    if (cur === targetId) break;
    for (const nb of adj.get(cur) ?? []) {
      if (!visited.has(nb)) {
        visited.add(nb);
        prev.set(nb, cur);
        queue.push(nb);
      }
    }
  }
  if (!visited.has(targetId)) return null;

  const path = new Set<string>();
  let node: string | undefined = targetId;
  while (node !== undefined) {
    path.add(node);
    node = prev.get(node);
  }
  return path;
}
